// Per-node executors (client-side execution, Phase 0).
// Each executor receives resolved inputs (keyed by input-handle id) + the node's config data,
// and returns outputs keyed by output-handle id. Keep these pure-ish: no store access.

import { aiModelsApi } from '@/lib/ai-models-api';
import { api } from '@/lib/api';
import { resolveText } from './refs';

export interface ExecContext {
  aiModels: typeof aiModelsApi;
  api: typeof api;
  /** All node outputs computed so far this run — for {nodeId} reference resolution. */
  outputs: Record<string, Record<string, unknown>>;
}

/** ctx for live-preview runs (see NodeSpec.livePreview): those executors must never touch ctx,
 *  so every accessor throws — a ctx-using executor mistakenly flagged livePreview fails loudly. */
export const PREVIEW_CONTEXT: ExecContext = {
  get aiModels(): never {
    throw new Error('live preview: executor tried to use ctx.aiModels');
  },
  get api(): never {
    throw new Error('live preview: executor tried to use ctx.api');
  },
  get outputs(): never {
    throw new Error('live preview: executor tried to use ctx.outputs');
  },
};

type Inputs = Record<string, unknown>;
type Data = Record<string, unknown>;

export type NodeExecutor = (
  inputs: Inputs,
  data: Data,
  ctx: ExecContext,
) => Promise<Record<string, unknown>>;

/** Normalize a base64 (or already-prefixed) string into a renderable data URI. */
export function toDataUri(b64: string): string {
  if (!b64) return '';
  return b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
}

/** Strip a data-URI prefix, returning raw base64 (what the AI-models methods send). */
export function stripDataUri(s: string): string {
  if (!s) return s;
  return s.startsWith('data:') ? s.split(',', 2)[1] ?? s : s;
}

/** Fetch a (browser-reachable) image URL and return it as a data URI. */
async function urlToDataUri(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`could not load image (${res.status})`);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(new Error('failed to read image'));
    fr.readAsDataURL(blob);
  });
}

/** Require a non-empty image input or throw a clear error. */
function requireImage(v: unknown): string {
  const s = typeof v === 'string' ? v : '';
  if (!s) throw new Error('image input is required — connect an image-producing node');
  return s;
}

/** Load an image source (data URI / URL) into an HTMLImageElement. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('could not load image'));
    im.src = src;
  });
}

/** Pure-browser resize via Canvas (no GPU / no server call). */
async function canvasResize(dataUri: string, w: number, h: number): Promise<string> {
  const img = await loadImage(dataUri);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/png');
}

/** Pure-browser crop via Canvas. rect is normalized (0..1) {x, y, w, h}. */
const clamp255 = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
const numOr = (v: unknown, def: number) => (v == null || v === '' ? def : Number(v));

/** Pure-browser color adjustment via Canvas filters + pixel ops (channels, normalize). */
async function canvasAdjust(dataUri: string, d: Record<string, unknown>): Promise<string> {
  const img = await loadImage(dataUri);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');

  const b = numOr(d.brightness, 100);
  const c = numOr(d.contrast, 100);
  const s = numOr(d.saturation, 100);
  const hue = numOr(d.hue, 0);
  const parts: string[] = [];
  if (b !== 100) parts.push(`brightness(${b}%)`);
  if (c !== 100) parts.push(`contrast(${c}%)`);
  if (s !== 100) parts.push(`saturate(${s}%)`);
  if (hue) parts.push(`hue-rotate(${hue}deg)`);
  if (d.grayscale) parts.push('grayscale(1)');
  if (d.invert) parts.push('invert(1)');
  ctx.filter = parts.length ? parts.join(' ') : 'none';
  ctx.drawImage(img, 0, 0);
  ctx.filter = 'none';

  const rG = numOr(d.rGain, 100) / 100;
  const gG = numOr(d.gGain, 100) / 100;
  const bG = numOr(d.bGain, 100) / 100;
  const needChannels = rG !== 1 || gG !== 1 || bG !== 1;
  if (needChannels || d.normalize) {
    const data = ctx.getImageData(0, 0, w, h);
    const px = data.data;
    if (needChannels) {
      for (let i = 0; i < px.length; i += 4) {
        px[i] = clamp255(px[i] * rG);
        px[i + 1] = clamp255(px[i + 1] * gG);
        px[i + 2] = clamp255(px[i + 2] * bG);
      }
    }
    if (d.normalize) {
      let mn = 255;
      let mx = 0;
      for (let i = 0; i < px.length; i += 4) {
        const l = (px[i] + px[i + 1] + px[i + 2]) / 3;
        if (l < mn) mn = l;
        if (l > mx) mx = l;
      }
      const range = mx - mn || 1;
      for (let i = 0; i < px.length; i += 4) {
        px[i] = clamp255(((px[i] - mn) * 255) / range);
        px[i + 1] = clamp255(((px[i + 1] - mn) * 255) / range);
        px[i + 2] = clamp255(((px[i + 2] - mn) * 255) / range);
      }
    }
    ctx.putImageData(data, 0, 0);
  }

  return canvas.toDataURL('image/png');
}

async function canvasCrop(
  dataUri: string,
  rect: { x: number; y: number; w: number; h: number },
): Promise<string> {
  const img = await loadImage(dataUri);
  const W = img.naturalWidth || img.width;
  const H = img.naturalHeight || img.height;
  const sx = Math.max(0, Math.round(rect.x * W));
  const sy = Math.max(0, Math.round(rect.y * H));
  const sw = Math.max(1, Math.min(W - sx, Math.round(rect.w * W)));
  const sh = Math.max(1, Math.min(H - sy, Math.round(rect.h * H)));
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas.toDataURL('image/png');
}

/** Draw an image onto a fresh canvas and return its 2d context + pixel buffer. */
function imageToPixels(img: HTMLImageElement): { w: number; h: number; px: Uint8ClampedArray } {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, 0, 0);
  return { w, h, px: ctx.getImageData(0, 0, w, h).data };
}

/** Crop to content: alpha bbox if the image has transparency, else trim a near-uniform corner color. */
async function canvasAutoTrim(dataUri: string, threshold: number): Promise<string> {
  const img = await loadImage(dataUri);
  const { w, h, px } = imageToPixels(img);
  let hasAlpha = false;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] < 250) {
      hasAlpha = true;
      break;
    }
  }
  const tol = Math.max(0, threshold);
  const br = px[0];
  const bg = px[1];
  const bb = px[2];
  const isContent = hasAlpha
    ? (i: number) => px[i + 3] > tol
    : (i: number) => Math.abs(px[i] - br) + Math.abs(px[i + 1] - bg) + Math.abs(px[i + 2] - bb) > tol * 3;

  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (isContent((y * w + x) * 4)) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return dataUri; // nothing detected — passthrough
  return canvasCrop(dataUri, {
    x: minX / w,
    y: minY / h,
    w: (maxX - minX + 1) / w,
    h: (maxY - minY + 1) / h,
  });
}

/** Center the image on a (square) transparent canvas with a margin, then resize. */
async function canvasPadFrame(
  dataUri: string,
  size: number,
  marginPct: number,
  square: boolean,
): Promise<string> {
  const img = await loadImage(dataUri);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const side = Math.max(w, h);
  const pad = Math.round(side * (marginPct / 100));
  const boxW = (square ? side : w) + pad * 2;
  const boxH = (square ? side : h) + pad * 2;
  const box = document.createElement('canvas');
  box.width = boxW;
  box.height = boxH;
  const bctx = box.getContext('2d');
  if (!bctx) throw new Error('canvas 2d context unavailable');
  bctx.drawImage(img, Math.round((boxW - w) / 2), Math.round((boxH - h) / 2));

  const out = document.createElement('canvas');
  if (square) {
    out.width = size;
    out.height = size;
  } else {
    const scale = size / Math.max(boxW, boxH);
    out.width = Math.max(1, Math.round(boxW * scale));
    out.height = Math.max(1, Math.round(boxH * scale));
  }
  const octx = out.getContext('2d');
  if (!octx) throw new Error('canvas 2d context unavailable');
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(box, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

/** Gaussian blur via the canvas filter (preserves alpha). */
async function canvasBlur(dataUri: string, radius: number): Promise<string> {
  const img = await loadImage(dataUri);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.filter = radius > 0 ? `blur(${radius}px)` : 'none';
  ctx.drawImage(img, 0, 0);
  ctx.filter = 'none';
  return canvas.toDataURL('image/png');
}

/**
 * Make a texture tile seamlessly: roll by half so the wrap-edges land in a
 * continuous interior region, then cross-blend the real image (crisp interior)
 * over the rolled copy (seamless edges) with a weight that falls to 0 at every
 * border. Mirrors tower-defense's make_seamless().
 */
async function canvasSeamless(dataUri: string, bandPct: number, denoise: number): Promise<string> {
  const src = denoise > 0 ? await canvasBlur(dataUri, denoise) : dataUri;
  const img = await loadImage(src);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, 0, 0);
  const src0 = ctx.getImageData(0, 0, w, h);
  const a = src0.data;
  const out = ctx.createImageData(w, h);
  const o = out.data;
  const ox = Math.floor(w / 2);
  const oy = Math.floor(h / 2);
  const bx = Math.max(1, Math.round((w * bandPct) / 100));
  const by = Math.max(1, Math.round((h * bandPct) / 100));
  for (let y = 0; y < h; y++) {
    const wy = Math.min(1, Math.min(y, h - 1 - y) / by);
    const sy = (y + oy) % h;
    for (let x = 0; x < w; x++) {
      const wx = Math.min(1, Math.min(x, w - 1 - x) / bx);
      const weight = Math.min(wx, wy);
      const i = (y * w + x) * 4;
      const j = (sy * w + ((x + ox) % w)) * 4;
      o[i] = a[i] * weight + a[j] * (1 - weight);
      o[i + 1] = a[i + 1] * weight + a[j + 1] * (1 - weight);
      o[i + 2] = a[i + 2] * weight + a[j + 2] * (1 - weight);
      o[i + 3] = 255;
    }
  }
  ctx.putImageData(out, 0, 0);
  return canvas.toDataURL('image/png');
}

// ===== Draw / procedural helpers =====

const intOr = (v: unknown, d: number) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? n : d;
};

function hexToRgb(hex: string): [number, number, number] {
  let h = (hex || '#000000').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return Number.isFinite(n) ? [(n >> 16) & 255, (n >> 8) & 255, n & 255] : [0, 0, 0];
}

/** CSS rgba() from a #hex + 0..255 alpha. */
function cssColor(hex: string, alpha = 255): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(255, alpha)) / 255})`;
}

function newCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  return [c, ctx];
}

/** Convert a mask image's luminance into a white canvas whose ALPHA = that luminance. */
function maskToAlpha(mask: HTMLImageElement, w: number, h: number, invert = false): HTMLCanvasElement {
  const [m, mctx] = newCanvas(w, h);
  mctx.drawImage(mask, 0, 0, w, h);
  const d = mctx.getImageData(0, 0, w, h);
  const p = d.data;
  for (let i = 0; i < p.length; i += 4) {
    let a = (p[i] + p[i + 1] + p[i + 2]) / 3;
    if (invert) a = 255 - a;
    p[i] = p[i + 1] = p[i + 2] = 255;
    p[i + 3] = a;
  }
  mctx.putImageData(d, 0, 0);
  return m;
}

export const EXECUTORS: Record<string, NodeExecutor> = {
  promptInput: async (inputs, data, ctx) => {
    // Resolve {input} (edge) and {nodeId(.handle)} references; {name} params are baked server-side.
    const text = resolveText(String(data.text ?? ''), inputs, ctx.outputs);
    return { text: text.trim() };
  },

  textInput: async (_inputs, data) => ({ text: String(data.text ?? '') }),

  numberInput: async (_inputs, data) => ({ value: Number(data.value ?? 0) }),

  generate: async (inputs, data, ctx) => {
    const prompt = String(inputs.prompt ?? '').trim();
    if (!prompt) throw new Error('prompt input is required — connect a Prompt node');
    const seed =
      inputs.seed != null ? Math.round(Number(inputs.seed)) : ((data.seed as number | null) ?? null);
    const res = await ctx.aiModels.imagegenGenerate({
      prompt,
      model: (data.model as string) ?? 'z-image-turbo',
      width: (data.width as number) ?? 1024,
      height: (data.height as number) ?? 1024,
      num_inference_steps: (data.num_inference_steps as number) ?? 9,
      seed,
    });
    return { image: toDataUri(res.image), _seed: res.seed, _ms: res.inference_time_ms };
  },

  imageUpload: async (_inputs, data) => {
    const image = (data.image as string) ?? '';
    if (!image) throw new Error('no image uploaded');
    return { image };
  },

  imageGallery: async (_inputs, data) => {
    const url = (data.imageUrl as string) ?? '';
    if (!url) throw new Error('no gallery image selected');
    return { image: await urlToDataUri(url) };
  },

  removeBackground: async (inputs, data, ctx) => {
    const img = requireImage(inputs.image);
    const r = await ctx.aiModels.rmbgRemove(stripDataUri(img), {
      model: (data.model as string) || undefined,
      alpha_matting: !!data.alpha_matting,
    });
    return { image: toDataUri(r.image), _ms: r.inference_time_ms };
  },

  upscale: async (inputs, data, ctx) => {
    const img = requireImage(inputs.image);
    const r = await ctx.aiModels.upscaleImage(stripDataUri(img), Number(data.scale ?? 2));
    return { image: toDataUri(r.image), _ms: r.inference_time_ms };
  },

  faceRestore: async (inputs, data, ctx) => {
    const img = requireImage(inputs.image);
    const r = await ctx.aiModels.gfpganRestore(stripDataUri(img), {
      upscale: Number(data.upscale ?? 2),
    });
    return { image: toDataUri(r.image), _ms: r.inference_time_ms, _faces: r.faces_restored };
  },

  depth: async (inputs, _data, ctx) => {
    const img = requireImage(inputs.image);
    const r = await ctx.aiModels.depthEstimate(stripDataUri(img));
    return { image: toDataUri(r.depth_map), _ms: r.inference_time_ms };
  },

  resize: async (inputs, data) => {
    const img = requireImage(inputs.image);
    const w = Math.max(1, Math.round(Number(data.width ?? 512)));
    const h = Math.max(1, Math.round(Number(data.height ?? 512)));
    return { image: await canvasResize(img, w, h) };
  },

  crop: async (inputs, data) => {
    const img = requireImage(inputs.image);
    const rect = (data.rect as { x: number; y: number; w: number; h: number }) ?? {
      x: 0,
      y: 0,
      w: 1,
      h: 1,
    };
    return { image: await canvasCrop(img, rect) };
  },

  autoTrim: async (inputs, data) => {
    const img = requireImage(inputs.image);
    return { image: await canvasAutoTrim(img, Number(data.threshold ?? 10)) };
  },

  padFrame: async (inputs, data) => {
    const img = requireImage(inputs.image);
    return {
      image: await canvasPadFrame(
        img,
        Math.max(1, Math.round(Number(data.size ?? 256))),
        Number(data.margin ?? 6),
        data.square !== false,
      ),
    };
  },

  blur: async (inputs, data) => {
    const img = requireImage(inputs.image);
    return { image: await canvasBlur(img, Number(data.radius ?? 1)) };
  },

  seamless: async (inputs, data) => {
    const img = requireImage(inputs.image);
    return { image: await canvasSeamless(img, Number(data.band ?? 16), Number(data.denoise ?? 0.6)) };
  },

  // ===== Draw / procedural =====
  solidColor: async (_inputs, data) => {
    const [c, ctx] = newCanvas(intOr(data.width, 512), intOr(data.height, 512));
    ctx.fillStyle = cssColor(String(data.color ?? '#000000'), numOr(data.alpha, 255));
    ctx.fillRect(0, 0, c.width, c.height);
    return { image: c.toDataURL('image/png') };
  },

  gradient: async (_inputs, data) => {
    const [c, ctx] = newCanvas(intOr(data.width, 512), intOr(data.height, 512));
    const type = String(data.type ?? 'vertical');
    let grad: CanvasGradient;
    if (type === 'radial')
      grad = ctx.createRadialGradient(c.width / 2, c.height / 2, 0, c.width / 2, c.height / 2, Math.max(c.width, c.height) / 2);
    else if (type === 'horizontal') grad = ctx.createLinearGradient(0, 0, c.width, 0);
    else if (type === 'diagonal') grad = ctx.createLinearGradient(0, 0, c.width, c.height);
    else grad = ctx.createLinearGradient(0, 0, 0, c.height);
    grad.addColorStop(0, cssColor(String(data.color1 ?? '#000000')));
    grad.addColorStop(1, cssColor(String(data.color2 ?? '#ffffff')));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, c.width, c.height);
    return { image: c.toDataURL('image/png') };
  },

  grid: async (_inputs, data) => {
    const [c, ctx] = newCanvas(intOr(data.width, 512), intOr(data.height, 512));
    const cell = Math.max(2, intOr(data.cell, 32));
    ctx.fillStyle = cssColor(String(data.color1 ?? '#e5e7eb'));
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = cssColor(String(data.color2 ?? '#9ca3af'));
    if (String(data.mode ?? 'checker') === 'checker') {
      for (let yy = 0; yy < c.height; yy += cell)
        for (let xx = 0; xx < c.width; xx += cell)
          if ((((xx / cell) | 0) + ((yy / cell) | 0)) % 2 === 1) ctx.fillRect(xx, yy, cell, cell);
    } else {
      const lw = Math.max(1, intOr(data.lineWidth, 2));
      for (let xx = 0; xx < c.width; xx += cell) ctx.fillRect(xx, 0, lw, c.height);
      for (let yy = 0; yy < c.height; yy += cell) ctx.fillRect(0, yy, c.width, lw);
    }
    return { image: c.toDataURL('image/png') };
  },

  noise: async (_inputs, data) => {
    const [c, ctx] = newCanvas(intOr(data.width, 512), intOr(data.height, 512));
    const amount = numOr(data.amount, 64);
    const mono = !!data.mono;
    const img = ctx.createImageData(c.width, c.height);
    const px = img.data;
    const rnd = () => 128 + (Math.random() - 0.5) * 2 * amount;
    for (let i = 0; i < px.length; i += 4) {
      if (mono) {
        const v = clamp255(rnd());
        px[i] = px[i + 1] = px[i + 2] = v;
      } else {
        px[i] = clamp255(rnd());
        px[i + 1] = clamp255(rnd());
        px[i + 2] = clamp255(rnd());
      }
      px[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    return { image: c.toDataURL('image/png') };
  },

  text: async (inputs, data) => {
    const txt = String(inputs.text ?? '');
    let w = intOr(data.width, 512);
    let h = intOr(data.height, 256);
    let base: HTMLImageElement | null = null;
    if (inputs.image) {
      base = await loadImage(requireImage(inputs.image));
      w = base.naturalWidth || base.width;
      h = base.naturalHeight || base.height;
    }
    const [c, ctx] = newCanvas(w, h);
    if (base) ctx.drawImage(base, 0, 0);
    const size = numOr(data.size, 48);
    ctx.font = `${size}px sans-serif`;
    ctx.textAlign = String(data.align ?? 'center') as CanvasTextAlign;
    ctx.textBaseline = 'middle';
    const cx = numOr(data.x, 0.5) * c.width;
    const cy = numOr(data.y, 0.5) * c.height;
    const lines = txt.split('\n');
    const lh = size * 1.2;
    const sw = numOr(data.strokeWidth, 0);
    lines.forEach((ln, i) => {
      const ly = cy - ((lines.length - 1) * lh) / 2 + i * lh;
      if (sw > 0) {
        ctx.lineWidth = sw * 2;
        ctx.lineJoin = 'round';
        ctx.strokeStyle = cssColor(String(data.strokeColor ?? '#000000'));
        ctx.strokeText(ln, cx, ly);
      }
      ctx.fillStyle = cssColor(String(data.color ?? '#ffffff'));
      ctx.fillText(ln, cx, ly);
    });
    return { image: c.toDataURL('image/png') };
  },

  shapes: async (inputs, data) => {
    let w = intOr(data.width, 512);
    let h = intOr(data.height, 512);
    let base: HTMLImageElement | null = null;
    if (inputs.image) {
      base = await loadImage(requireImage(inputs.image));
      w = base.naturalWidth || base.width;
      h = base.naturalHeight || base.height;
    }
    const [c, ctx] = newCanvas(w, h);
    if (base) ctx.drawImage(base, 0, 0);
    const x1 = numOr(data.x1, 0.2) * c.width;
    const y1 = numOr(data.y1, 0.2) * c.height;
    const x2 = numOr(data.x2, 0.8) * c.width;
    const y2 = numOr(data.y2, 0.8) * c.height;
    const sw = numOr(data.strokeWidth, 0);
    const fillA = numOr(data.fillAlpha, 255);
    ctx.fillStyle = cssColor(String(data.fill ?? '#000000'), fillA);
    ctx.strokeStyle = cssColor(String(data.stroke ?? '#000000'));
    ctx.lineWidth = Math.max(1, sw);
    const shape = String(data.shape ?? 'rectangle');
    if (shape === 'line') {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    } else if (shape === 'ellipse') {
      ctx.beginPath();
      ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, Math.PI * 2);
      if (fillA > 0) ctx.fill();
      if (sw > 0) ctx.stroke();
    } else {
      const rx = Math.min(x1, x2);
      const ry = Math.min(y1, y2);
      if (fillA > 0) ctx.fillRect(rx, ry, Math.abs(x2 - x1), Math.abs(y2 - y1));
      if (sw > 0) ctx.strokeRect(rx, ry, Math.abs(x2 - x1), Math.abs(y2 - y1));
    }
    return { image: c.toDataURL('image/png') };
  },

  outline: async (inputs, data) => {
    const img = await loadImage(requireImage(inputs.image));
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const rad = Math.max(1, intOr(data.width, 4));
    // Colored silhouette of the subject (image masked to its own alpha → flat color).
    const [s, sctx] = newCanvas(w, h);
    sctx.drawImage(img, 0, 0);
    sctx.globalCompositeOperation = 'source-in';
    sctx.fillStyle = cssColor(String(data.color ?? '#000000'), numOr(data.alpha, 255));
    sctx.fillRect(0, 0, w, h);
    // Stamp the silhouette in a ring, then the original on top.
    const [c, ctx] = newCanvas(w + rad * 2, h + rad * 2);
    const steps = 16;
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * Math.PI * 2;
      ctx.drawImage(s, rad + Math.cos(a) * rad, rad + Math.sin(a) * rad);
    }
    ctx.drawImage(img, rad, rad);
    return { image: c.toDataURL('image/png') };
  },

  composite: async (inputs, data) => {
    const bg = await loadImage(requireImage(inputs.background));
    const fg = await loadImage(requireImage(inputs.foreground));
    const W = bg.naturalWidth || bg.width;
    const H = bg.naturalHeight || bg.height;
    const scale = numOr(data.scale, 1);
    // Foreground layer (scaled, optionally masked).
    const [fl, flctx] = newCanvas((fg.naturalWidth || fg.width) * scale, (fg.naturalHeight || fg.height) * scale);
    flctx.drawImage(fg, 0, 0, fl.width, fl.height);
    if (inputs.mask) {
      const mask = await loadImage(requireImage(inputs.mask));
      flctx.globalCompositeOperation = 'destination-in';
      flctx.drawImage(maskToAlpha(mask, fl.width, fl.height), 0, 0);
      flctx.globalCompositeOperation = 'source-over';
    }
    const [c, ctx] = newCanvas(W, H);
    ctx.drawImage(bg, 0, 0);
    // Blend mode -> canvas globalCompositeOperation ('add' uses the additive 'lighter' op).
    const gco: Record<string, GlobalCompositeOperation> = {
      multiply: 'multiply', screen: 'screen', overlay: 'overlay', darken: 'darken',
      lighten: 'lighten', 'soft-light': 'soft-light', 'hard-light': 'hard-light',
      difference: 'difference', add: 'lighter',
    };
    const op = gco[String(data.blend ?? 'normal')];
    if (op) ctx.globalCompositeOperation = op;
    ctx.globalAlpha = Math.max(0, Math.min(100, numOr(data.opacity, 100))) / 100;
    ctx.drawImage(fl, numOr(data.x, 0.5) * W - fl.width / 2, numOr(data.y, 0.5) * H - fl.height / 2);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    return { image: c.toDataURL('image/png') };
  },

  applyMask: async (inputs, data) => {
    const img = await loadImage(requireImage(inputs.image));
    const mask = await loadImage(requireImage(inputs.mask));
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const [c, ctx] = newCanvas(w, h);
    ctx.drawImage(img, 0, 0);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(maskToAlpha(mask, w, h, !!data.invert), 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    return { image: c.toDataURL('image/png') };
  },

  adjustColors: async (inputs, data) => ({
    image: await canvasAdjust(requireImage(inputs.image), data),
  }),

  detect: async (inputs, data, ctx) => {
    const img = requireImage(inputs.image);
    const r = await ctx.aiModels.yoloDetect(stripDataUri(img), {
      confidence: Number(data.confidence ?? 0.25),
      return_annotated: true,
    });
    const dets = r.detections ?? [];
    return {
      detections: dets,
      image: r.annotated_image ? toDataUri(r.annotated_image) : img,
      _info: `${dets.length} detection${dets.length === 1 ? '' : 's'}`,
      _ms: r.inference_time_ms,
    };
  },

  segment: async (inputs, _data, ctx) => {
    const img = requireImage(inputs.image);
    const boxes = inputs.boxes as
      | Array<{ bbox: { x1: number; y1: number; x2: number; y2: number } }>
      | undefined;
    if (!boxes || !boxes.length) {
      throw new Error('boxes input required — connect a Detect node');
    }
    const r = await ctx.aiModels.sam2SegmentBox(stripDataUri(img), boxes[0].bbox);
    if (!r.masks?.length) throw new Error('no mask produced');
    const best = r.masks[0];
    return {
      mask: toDataUri(best.mask),
      _info: `${boxes.length} box(es) · score ${(best.score ?? 0).toFixed(2)}`,
      _ms: r.inference_time_ms,
    };
  },

  caption: async (inputs, _data, ctx) => {
    const img = requireImage(inputs.image);
    const r = await ctx.aiModels.captionGenerate(stripDataUri(img));
    const text = r.caption ?? '';
    return { text, _info: text, _ms: r.inference_time_ms };
  },

  textJoin: async (inputs, data) => {
    const sep = String(data.separator ?? ', ');
    const parts = ['in1', 'in2', 'in3', 'in4']
      .map((k) => inputs[k])
      .filter((v) => v != null && String(v) !== '')
      .map(String);
    const text = parts.join(sep);
    return { text, _info: text.slice(0, 120) };
  },

  pick: async (inputs, data) => {
    const raw = inputs.index != null ? Number(inputs.index) : Number(data.index ?? 1);
    const idx = Math.max(1, Math.min(4, Math.round(raw) || 1));
    const val = inputs[`in${idx}`];
    const out: Record<string, unknown> = { out: val };
    if (typeof val === 'string' && val.startsWith('data:')) out.image = val;
    else if (val != null && val !== '') out._info = `[${idx}] ${String(val).slice(0, 100)}`;
    return out;
  },

  saveToGallery: async (inputs, data, ctx) => {
    const img = requireImage(inputs.image);
    const seriesId = (data.series_id as number | null) ?? null;
    const res = await ctx.api.saveImage(img, seriesId); // backend parses the data URI
    return { image: img, filename: res.filename };
  },

  animateVideo: async () => {
    // Video generation is heavy/slow — only run it server-side.
    throw new Error('Video runs on the server — switch the Run mode toggle to "server"');
  },

  preview: async (inputs) => {
    const v = inputs.image;
    if (typeof v === 'string') {
      if (/^data:video/.test(v)) return { video: v };
      if (/^data:/.test(v) || /^(iVBOR|\/9j\/|UklGR|R0lGOD)/.test(v)) return { image: v };
      if (v !== '') return { text: v };
    } else if (v != null) {
      return { text: String(v) };
    }
    return {};
  },
};
